import {SessionStateBase} from '../../../models/LiveSessionState';
import {observable, computed, runInAction} from 'mobx';
import {IWifiAnalyzerResults} from '../../../services/AngularServices/WifiAnalyzerService';
import {CONNECTION_STATUS} from '../../../services/ts-locale-helper/ts-locale-helper.settings';
import {MeetingMode, AddressTypesEnum, RECORDING_STATUS} from '@techsee/techsee-common/lib/constants/room.constants';
import {IMAGE_MODES, IOS_DEVICES, VIDEO_MODES} from '../../../../states/dashboard/main/main.settings';
import VideoModeState from '../dashboard-state/video-mode.state';
import {RightBarState} from '../dashboard-state/right-bar.state';
import FloatingToolbarState from '../dashboard-state/floatingToolbar.state';
import {CameraZoomController} from '../controllers/camera-zoom.controller';
import {AcceptTermsModalState} from '../../../modals/accept-terms-modal/AcceptTermsModal.state';
import {ChangeSettingsModalState} from '../../../modals/change-settings-modal/ChangeSettingsModal.state';
import {SmsStatusModalState} from '../../../modals/sms-status-modal/SmsStatusModal.state';
import {ChangeAccountModalState} from '../../../modals/change-account-modal/ChangeAccountModal.state';
import {ImageEditorState} from '../../../components/ts-image-editor/ts-image-editor.state';
import {
    PlatformType,
    VisualScanStatusType,
    WifiScanStatusType
} from '@techsee/techsee-common/lib/constants/utils.constant';
import {ConnectionTypesEnum} from '../../../services/Session/SessionContracts';
import includes from 'lodash/includes';
import get from 'lodash/get';
import find from 'lodash/find';
import {ClientRoomStorage} from './ClientRoomStorage';
import {DashboardRoomStorage} from './DashboardRoomStorage';
import {isRemoteClientAndroid} from '../../../utils/utils';
import {LivePointerRoomStorage} from './LivePointerRoomStorage';
import {PHOTO_SELECTION_STATE, CLIENT_IN_PHOTO_MODE_STATES} from '../../../../states/dashboard/main/main.settings';
import {VIDEO_MINIMIZED_MODES} from '../../../../states/dashboard/main/main.helper';
import {VJ_FLOW_ALIAS_DELIMITER} from '@techsee/techsee-common/lib/constants/visual-journey.constants';
// @ts-ignore
import utils, {isAudioAvailableInMode} from '@techsee/techsee-common/lib/utils';
import {MediaServiceType} from '@techsee/techsee-media-service/lib/MediaConstants';
import {VideoControlState} from '../dashboard-state/video-control.state';

export interface IMessageAlert {
    display: boolean;
    color: string;
    label: string;
}

export class DashboardState extends SessionStateBase {
    @observable dashboardControllerLoaded: boolean = false;

    @observable currentUserId: string = '';

    @observable showLocationPopover: boolean = false;

    @observable mobilePopoverLocation: string = '';

    @observable displayObserverSendingForm: boolean = false;

    @observable observationCurrentTab: string = '';

    @observable phoneToInviteObserver: string = '';

    @observable emailToInviteObserver: string = '';

    @observable isOfflineRoom: boolean = false;

    @observable clientOs: string = '';

    @observable isObserve: boolean = false;

    @observable canvasReadOnly: boolean = false;

    @observable isOfflineMode: boolean = false;

    @observable isLibraryTaggingMode: boolean = false;

    @observable connectionStatus: number = CONNECTION_STATUS.WAITING;

    @observable mediaServiceType: MediaServiceType = MediaServiceType.NONE;

    @observable isOpentok: boolean = false;

    @observable isMediaServer: boolean = false;

    @observable isLeftBarClosed: boolean = true;

    @observable notificationSmsInProgress: boolean = false;

    @observable isClientRoomStorageSynced: boolean = false;

    @observable currentResource?: any; // todo type

    @observable imageMode?: string | null; // IMAGE_MODES.SCANNING, etc.

    @observable meetingMode?: string; // Synced from clientState.mode

    @observable offlineMode: boolean = false;

    @observable hideWifiScan: boolean = true;

    @observable allowSessionSummary: boolean = false;

    @observable mainLoaded: boolean = false;

    readonly wifiState: WiFiAnalyzerState = new WiFiAnalyzerState();

    readonly acceptTermsModalState: AcceptTermsModalState = new AcceptTermsModalState();
    readonly changeSettingsModalState: ChangeSettingsModalState = new ChangeSettingsModalState();
    readonly smsStatusModalState: SmsStatusModalState = new SmsStatusModalState();
    readonly changeAccountModalState: ChangeAccountModalState = new ChangeAccountModalState();

    @observable isDeviceSupportsVisualScan: boolean = true;

    isScanIdExist?: boolean;

    readonly floatingToolbarState: FloatingToolbarState;

    readonly cameraZoomController: CameraZoomController;

    readonly videoModeState: VideoModeState = new VideoModeState();

    readonly rightBarState: RightBarState = new RightBarState();

    readonly imageEditorState: ImageEditorState = new ImageEditorState();

    readonly dashboardRoomStorage: DashboardRoomStorage = new DashboardRoomStorage();

    readonly livePointerRoomStorage: LivePointerRoomStorage = new LivePointerRoomStorage();

    readonly videoControlState: VideoControlState = new VideoControlState();

    @observable clientType?: string;

    @observable clientInPhotoModeState?: string;

    @observable screenshots: any[] = [];

    @observable areBothSidesConnected: boolean = false;

    @observable isSquareFloatingVideo: boolean = false;

    @observable returnToOneClickGuidance: boolean = false;

    @observable scanId?: string;

    @observable useNewInterface: boolean = false;

    @observable isWifiScanActive?: boolean;

    @observable canvasReady: boolean = false;
    @observable canvasIsFresh: boolean = false;
    @observable isAnnotated: boolean = false;
    @observable areTagsChanged: boolean = false;
    @observable endedTheMeeting: boolean = false;
    @observable customerEndedTheMeeting: boolean = false;
    @observable rejectedTermsAndConditions: boolean = false;
    @observable screenShareIOS: boolean = false;
    @observable messageAlert: IMessageAlert | null = null;

    @observable lastEvent: {text?: string; timestamp?: number; order?: number} | null = null;

    @observable cameraApprovalIndicationNotRequired: boolean = false;

    @observable showWizard: boolean = false;

    @observable multipleImagesSharingEnabled: boolean = false;

    @observable isHighImageSharingQuality: boolean = false;

    @observable enableSharingImagesContainer: boolean = false;

    @observable enableLocalImageSaving: boolean = false;

    @observable enableCopyToClipboard: boolean = false;

    @observable enableEmailImageSharing: boolean = false;

    @observable downloadAndEmailSharing: boolean = false;

    @observable isQuickShareEnabled: boolean = false;

    @observable showLoader: boolean = false;

    @observable hideWidgets: boolean = false;

    @observable drawerOpen: boolean = false;

    @observable mobileMenuDropdownOpen: boolean = false;

    @observable miniMenuDropdownOpen: boolean = false;

    @observable miniDashboardActiveTool: string = 'arrow';

    @observable miniDashboardActiveColor: string = '#ff0000';

    @observable modesEnabled: boolean = true;

    @observable allowUploadImageFileFromDashboard: boolean = false;

    @observable interactionSummaryPreviews: any[] = [];

    @observable isInteractionSummaryReady: boolean = false;

    @observable sophieCtx: any;
    @observable preloadedVideo: any;
    @observable allowScreenShare: boolean = false;
    @observable audioRequestedFromUrl: boolean = false;
    @observable roomCode: string = '';

    public currentUser: any;
    public roomData: any;
    public embeddedInfo: {isMiniDashboard: any; isEmbeddedDashboard: any} | null = null;

    public accountData: any;
    clientRoomStorage: ClientRoomStorage;

    constructor(accountData: any, currentUser: any, roomData: any, clientRoomStorage: ClientRoomStorage) {
        super();

        this.accountData = accountData;
        this.currentUser = currentUser;
        this.roomData = roomData;
        this.clientRoomStorage = clientRoomStorage;
        this.floatingToolbarState = new FloatingToolbarState(this);
        this.cameraZoomController = new CameraZoomController(this);
        this.initFromData();
    }

    private initFromData(): void {
        const roomData = this.roomData;

        if (!roomData) {
            return;
        }

        runInAction(() => {
            this.sessionInfo.sessionRoomId = roomData._id;
            this.sessionInfo.sessionLink = roomData.sentURL;
            this.sessionInfo.isSessionActive = true;
            this.sessionInfo.isOffline = roomData.offline;
            this.sessionInfo.clientJoinedSession = !!roomData.clientDeviceBrowser;
            this.sessionInfo.startWithAgentType = roomData.startWithAgentType;
            this.isWifiScanActive = get(roomData, 'isWifiScanActive', false);

            this.clientMobileType = roomData.clientDevice;
            this.clientMobileBrowser = roomData.clientDeviceBrowser;
            this.clientMobileOsName = get(roomData, 'clientOsObject.name');
            this.clientMobileOsVersion = get(roomData, 'clientOsObject.version');
            this.inviteMethodInfo.customerRef = roomData.customerId;
            this.inviteMethodInfo.addressType =
                roomData.customerEmail !== 'none' ? AddressTypesEnum.EMAIL : AddressTypesEnum.SMS;
            this.inviteMethodInfo.address =
                roomData.customerEmail !== 'none' ? roomData.customerEmail : roomData.customerNumber;
            this.inviteMethodInfo.clientLanguage = roomData.clientLanguage;
            this.clientType = roomData.clientType;

            this.clientNetworkInfo.connectionType = get(roomData, 'mobileNetworkInfo.connectionType')
                ? get(roomData, 'mobileNetworkInfo.connectionType').toUpperCase()
                : ConnectionTypesEnum.NETWORK;
            this.clientNetworkInfo.downlinkMax = get(roomData, 'mobileNetworkInfo.downlinkMax') || 0;

            this.allowScreenShare =
                get(this.accountData, 'protectedSettings.liveSdk.enabled') &&
                (utils as any).isModeSupportNativeVideoSharing(roomData.startWithAgentType);

            const enableShareMenu = get(this.accountData, 'protectedSettings.enableShareMenu');

            this.multipleImagesSharingEnabled =
                enableShareMenu && get(this.accountData, 'protectedSettings.multipleImagesSharingEnabled');
        });
    }

    @computed
    get isScanMode() {
        return this.videoModeState.videoState.mode === VIDEO_MODES.SCANNING || this.imageMode === IMAGE_MODES.SCANNING;
    }

    @computed
    get isScreenMirroring() {
        return this.meetingMode === MeetingMode.screen && !this.isDesktopSharingSession;
    }

    @computed
    get isDesktopSharingSession() {
        return this.meetingMode === MeetingMode.screen && this.clientType === PlatformType.desktop_web;
    }

    @computed
    get showImageWidget() {
        const videoFocus = this.videoModeState.videoState.focus;
        const videoEnabled = this.videoModeState.videoState.enabled;
        const condition1 = !videoFocus || !videoEnabled;
        const isLibraryTaggingMode = this.isLibraryTaggingMode;
        const hasPreloadedImage = this.videoModeState.preloadedImage?.resource;
        const condition2 = !(isLibraryTaggingMode && !hasPreloadedImage);
        const videoIsActive = this.videoModeState.isActive;
        const condition3 = !videoIsActive;

        return condition1 && condition2 && condition3;
    }

    @computed
    get isCanvas() {
        return (
            !this.isScanMode &&
            !(
                this.videoModeState.videoState.focus &&
                (this.meetingMode === MeetingMode.video || this.isScreenMirroring)
            ) &&
            this.showImageWidget
        );
    }

    @computed get clientDisconnected() {
        return !this.clientRoomStorage.connected;
    }

    @computed
    get isImagesOrOneClickMode() {
        return includes([MeetingMode.images, MeetingMode.oneClick], this.clientRoomStorage.mode);
    }

    @computed
    get isCoBrowsingMode() {
        return includes([MeetingMode.coBrowsing], this.clientRoomStorage.mode);
    }

    @computed
    get displayLoader() {
        return (
            this.showLoader &&
            !this.clientDisconnected &&
            this.clientRoomStorage.visible &&
            this.hideWidgets &&
            this.useNewInterface &&
            !this.isInInitiationPhase &&
            !this.isCoBrowsingMode
        );
    }

    @computed
    get isOnEditMode() {
        return !this.videoModeState.videoState.focus && this.currentResource && !this.videoControlState.isActive;
    }

    @computed
    get isMiniDashboardLoading() {
        const mode = this.clientRoomStorage.mode;

        const isIdleMode = this.connectionStatus === CONNECTION_STATUS.IDLE;
        const isOnEditMode = this.isOnEditMode;

        if (
            !isOnEditMode &&
            (mode === MeetingMode.video || mode === MeetingMode.screen) &&
            (!this.videoModeState.videoState.focus || !this.videoModeState.videoState.enabled)
        ) {
            return true;
        }

        return (
            mode !== MeetingMode.screen &&
            !isOnEditMode &&
            (isIdleMode || this.videoModeState.videoState.isClientVisible === false)
        );
    }

    @computed
    get isAndroidOrIphone() {
        const deviceInfo = this.clientRoomStorage.deviceInfo;

        if (deviceInfo && deviceInfo.match(/iPhone|iPad/)) {
            return IOS_DEVICES.APPLE;
        }

        if (isRemoteClientAndroid(this.clientMobileOsName)) {
            return IOS_DEVICES.ANDROID;
        }

        return IOS_DEVICES.APPLE;
    }

    @computed
    get isTitleActionHidden() {
        const screenSharePause =
            (!get(this.accountData, 'protectedSettings.liveSdk.allowScreenSharingPaused') &&
                includes([MeetingMode.appSharing], this.videoModeState.videoState.mode)) ||
            this.isScreenSharing;
        // Video paused by customer - isPaused === true and not paused by dashboard
        // Also prevent hiding action button when video paused but we are in canvas
        const videoPausedByCustomer =
            this.videoModeState.videoState.isPaused &&
            !this.videoModeState.videoState.videoPausedByDashboard &&
            !this.isCanvas;
        // Canvas mode - isCanvas === true and alos we are in ONE_CLICK or PHOTO modes
        const isCanvas =
            this.isCanvas && (this.meetingMode === MeetingMode.oneClick || this.meetingMode === MeetingMode.images);

        // X button should be HIDDEN (action blue button) when:
        // Current mode is sharing, video, video paused by customer or canvas

        return this.isDesktopSharing || screenSharePause || videoPausedByCustomer || isCanvas;
    }

    @computed
    get isScreenSharing() {
        return (
            !this.isCanvas &&
            !this.isScanMode &&
            this.meetingMode === MeetingMode.screen &&
            this.sessionInfo.startWithAgentType === MeetingMode.screen &&
            !this.isDesktopSharingSession
        );
    }

    @computed
    get isApplicationSharing() {
        return (
            !this.isCanvas &&
            !this.isScanMode &&
            this.meetingMode === MeetingMode.appSharing &&
            this.sessionInfo.startWithAgentType === MeetingMode.appSharing
        );
    }

    // Session type only, unlike isApplicationSharing which also reflects presentation state
    // (isCanvas/isScanMode) and therefore drops to false in edit mode
    @computed
    get isApplicationSharingMode() {
        return this.meetingMode === MeetingMode.appSharing;
    }

    @computed
    get isDesktopSharing() {
        return !this.isCanvas && !this.isScanMode && this.isDesktopSharingSession;
    }

    @computed
    get isVideoApplication() {
        return (
            this.meetingMode === MeetingMode.videoApplication &&
            this.sessionInfo.startWithAgentType === MeetingMode.videoApplication
        );
    }

    @computed
    get isVideoApplicationMode() {
        return this.meetingMode === MeetingMode.videoApplication;
    }

    @computed
    get isLiveVideo() {
        return (
            this.meetingMode === MeetingMode.video &&
            this.videoModeState.videoState.focus &&
            this.videoModeState.videoState.mode === VIDEO_MODES.CURSOR_SHARING &&
            this.videoModeState.videoState.mode !== VIDEO_MODES.SCANNING
        );
    }

    @computed
    get displayPhotoStreamGuidnace() {
        return (
            this.clientRoomStorage.photoSelectionDialogState === PHOTO_SELECTION_STATE.IN_PHOTO_CHAT &&
            (this.displayTermsInPhotoStream ||
                !this.videoModeState.wasInVideoMode ||
                this.clientInPhotoModeState === CLIENT_IN_PHOTO_MODE_STATES.FIRST_TIME)
        );
    }

    @computed
    get displayTermsInPhotoStream() {
        return get(this.accountData, 'settings.displayTermsInPhotoStream');
    }

    @computed
    get allowOneClickPhotoMode() {
        return get(this.accountData, 'settings.allowOneClickPhotoMode');
    }

    @computed
    get displayOneClickGuidnace() {
        const allowOneClickPhotoMode = this.allowOneClickPhotoMode;
        const isOneClickMode = this.clientRoomStorage.mode === MeetingMode.oneClick;
        const screenshotsLength = this.screenshots.length;
        const hasNoScreenshots = screenshotsLength === 0;
        const currentResourceUrl = this.currentResource?.url;
        const hasNoCurrentResource = !currentResourceUrl;
        const areBothSidesConnected = this.areBothSidesConnected;

        return (
            allowOneClickPhotoMode &&
            isOneClickMode &&
            hasNoScreenshots &&
            hasNoCurrentResource &&
            areBothSidesConnected
        );
    }

    @computed
    get isInGuidnace() {
        return this.displayPhotoStreamGuidnace || this.displayOneClickGuidnace;
    }

    @computed
    get isPhotoTitle() {
        return (
            (this.meetingMode === MeetingMode.oneClick || this.meetingMode === MeetingMode.images) &&
            !this.screenshots.length
        );
    }

    @computed
    get isToggleEnabled_StretchedVideoMode(): boolean {
        return get(this.accountData, 'protectedSettings.enableStretchedVideoMode', false);
    }

    @computed
    get isNativeFullAssist() {
        return get(this.accountData, 'protectedSettings.liveSdk.enableNativeFullAssist', false);
    }

    @computed
    get enableAgentWifiScanHistory() {
        return (
            (get(this.accountData, 'protectedSettings.connectivityGuru.enableAgentWifiScan', false) &&
                this.isNativeFullAssist &&
                this.isWifiScanActive) ||
            !!this.scanId ||
            this.shouldShowWifiScanHistoryInAllModes
        );
    }

    @computed
    get shouldShowWifiScanHistoryInAllModes(): boolean {
        return get(this.accountData, 'protectedSettings.connectivityGuru.shouldShowWifiScanHistoryInAllModes', false);
    }

    @computed
    get enableRounded() {
        return get(this.accountData, 'protectedSettings.enableRoundedMinimizedVideo');
    }

    @computed
    get minimizedVideoMode() {
        if (this.isToggleEnabled_StretchedVideoMode && this.enableRounded && !this.isSquareFloatingVideo) {
            return VIDEO_MINIMIZED_MODES.CIRCLE;
        } else if (this.enableAgentWifiScanHistory && this.isSquareFloatingVideo) {
            return VIDEO_MINIMIZED_MODES.SQUARE;
        }
        return undefined;

        // return VIDEO_MINIMIZED_MODES.CIRCLE
    }
    @computed
    get isToggleEnabled_RoundedFloatingVideo() {
        return this.minimizedVideoMode === VIDEO_MINIMIZED_MODES.CIRCLE;
    }

    @computed
    get isToggleEnabled_ScanCaptureDesign() {
        return (
            this.isToggleEnabled_StretchedVideoMode &&
            get(this.accountData, 'protectedSettings.enableScanCaptureDesign')
        );
    }

    @computed
    get isEnableWifiScan() {
        const isEnableAgentWifiScan =
            get(this.accountData, 'protectedSettings.connectivityGuru.enableAgentWifiScan', false) &&
            get(this.accountData, 'protectedSettings.liveSdk.enableNativeFullAssist', false) &&
            this.isWifiScanActive;

        // need to add a conditional for live 2.0 and on live session (not offline)
        return (
            (!this.isObserve &&
                isEnableAgentWifiScan &&
                this.clientType === PlatformType.mobile_native &&
                this.clientRoomStorage.connected &&
                this.clientRoomStorage.mode === MeetingMode.screen) ||
            (isEnableAgentWifiScan && this.clientRoomStorage.mode === MeetingMode.videoApplication)
        );
    }

    @computed
    get isEnableVisualScan() {
        const isEnableAgentVisualScan =
            get(this.accountData, 'protectedSettings.connectivityGuru.enableVisualAnalysis', false) &&
            get(this.accountData, 'protectedSettings.liveSdk.enableNativeFullAssist', false) &&
            this.isWifiScanActive;

        return (
            (!this.isObserve &&
                isEnableAgentVisualScan &&
                this.clientType === PlatformType.mobile_native &&
                this.clientRoomStorage.mode === MeetingMode.screen) ||
            (isEnableAgentVisualScan && this.clientRoomStorage.mode === MeetingMode.videoApplication)
        );
    }

    @computed
    get isScreenVideo() {
        return (
            this.isApplicationSharing &&
            this.videoModeState.videoState.focus &&
            this.videoModeState.videoState.mode === VIDEO_MODES.CURSOR_SHARING &&
            this.videoModeState.videoState.mode !== VIDEO_MODES.SCANNING
        );
    }

    @computed
    get displayLoaderReconnecting() {
        // Product request - for old interface do not show loader on image upload / one-click modes
        const enableIdleModeUsability = get(this.accountData, 'protectedSettings.enableIdleModeUsability');
        const allowNotConnected = this.isDesktopSharingSession;
        const useNewInterface = this.useNewInterface;
        const areBothSidesConnected = this.areBothSidesConnected;
        const clientMode = this.clientRoomStorage.mode;
        const isOneClickOrImages = [MeetingMode.oneClick, MeetingMode.images].includes(clientMode);

        if ((!useNewInterface || areBothSidesConnected) && isOneClickOrImages && !enableIdleModeUsability) {
            return false;
        }

        const isNotConnected = !this.clientRoomStorage.connected || this.connectionStatus === CONNECTION_STATUS.IDLE;

        const result =
            isNotConnected &&
            !allowNotConnected &&
            !this.endedTheMeeting &&
            !this.customerEndedTheMeeting &&
            !this.rejectedTermsAndConditions &&
            !this.isLibraryTaggingMode &&
            !this.isOfflineRoom &&
            !this.offlineMode &&
            !this.screenShareIOS;

        return result;
    }

    @computed
    get isTitleShown() {
        // The title should be shown, when:
        // we are in live video and if video paused then it should be paused by dashboard;
        // if StretchedVideoMode and RoundedFloatingVideo are turned on the show the title;
        // Not show the title if guidance showing;
        // Show the title only if not in guidance and canvasReady or videoReady
        // (because, after session ended, the guidance props in some cases is true, but not canvasReady or videoReady);
        // if ScanCaptureDesign is turned on and we are in scan mode then show the title;

        const isSharingMode =
            this.isScreenSharing || this.isApplicationSharing || this.isDesktopSharing || this.isVideoApplication;
        const isCanvas = this.isCanvas && !this.offlineMode;
        return (
            (((this.isLiveVideo || isCanvas || isSharingMode) &&
                this.isToggleEnabled_StretchedVideoMode &&
                this.isToggleEnabled_RoundedFloatingVideo &&
                !this.isInGuidnace &&
                (this.canvasReady ||
                    (this.videoModeState.videoState.focus && this.videoModeState.videoState.enabled))) ||
                (this.isScanMode && this.isToggleEnabled_ScanCaptureDesign) ||
                this.isPhotoTitle) &&
            !this.isEnableWifiScan
        );
    }

    @computed
    get extraStyle() {
        return (
            this.videoModeState.videoState.isPaused &&
            !this.videoModeState.videoState.videoPausedByDashboard &&
            this.isLiveVideo
        );
    }

    @computed
    get actionClasses() {
        return {
            live: this.isLiveVideo || this.isScreenVideo || this.isDesktopSharing || this.isVideoApplication,
            scan: this.isScanMode,
            canvas: this.isCanvas,
            paused: this.videoModeState.videoState.isPaused
        };
    }

    @computed
    get hasScreenshots(): boolean {
        return this.screenshots.length > 0;
    }

    @computed
    get showMultipleResourcesShareForImages(): boolean {
        return this.multipleImagesSharingEnabled && this.useNewInterface && this.hasScreenshots;
    }

    @computed
    get displayImageEditTools() {
        return (
            !(get(this.accountData, 'protectedSettings.enableSessionToolbar') && this.displayFloatingToolbar) &&
            get(this.currentResource, 'url') &&
            (!this.videoModeState.videoState.focus || !this.videoModeState.videoState.enabled) &&
            !get(this.currentResource, 'isVideo') &&
            !this.isObserve &&
            this.canvasReady
        );
    }

    @computed
    get observerAudioMuted(): boolean {
        const observer = find(this.dashboardRoomStorage.observersAudioMuted, (o) => o._id === this.currentUserId);

        return observer ? !!observer.audioMuted : true;
    }

    @computed
    get displayFloatingToolbar() {
        if (this.endedTheMeeting) {
            return this.displayMainContent;
        }

        if ([MeetingMode.oneClick, MeetingMode.images].some((mode) => this.clientRoomStorage.mode === mode)) {
            return this.displayMainContent;
        }

        return (
            [MeetingMode.video, MeetingMode.screen, MeetingMode.appSharing, MeetingMode.videoApplication].some(
                (mode) => this.clientRoomStorage.mode === mode
            ) && this.displayMainContent
        );
    }

    @computed
    get displayMainContent() {
        return (!this.showWizard || this.offlineMode || this.showPhotoSelectionDialog) && !this.isInInitiationPhase;
    }

    @computed
    get showPhotoSelectionDialog() {
        const isPhotoChat = this.clientRoomStorage.mode === MeetingMode.images;
        const photoSelectionDialogState = this.clientRoomStorage.photoSelectionDialogState;
        const currentResourceUrl = get(this.currentResource, 'url');

        return isPhotoChat && !!photoSelectionDialogState && !currentResourceUrl;
    }

    @computed
    get isInInitiationPhase() {
        // Use clientRoomStorage for reactive MobX properties
        return (
            this.clientRoomStorage.isReviewingTOS ||
            this.clientRoomStorage.isOnPreCameraApprovalScreen ||
            (this.clientRoomStorage.inCameraApprovalDialog && !this.cameraApprovalIndicationNotRequired)
        );
    }

    @computed
    get displayAudioButton() {
        return (
            isAudioAvailableInMode(this.clientRoomStorage.mode) &&
            this.dashboardRoomStorage.audioSupport &&
            this.clientRoomStorage.audioSupport &&
            this.dashboardRoomStorage.audioHandshakeSuccess !== false &&
            this.clientRoomStorage.audioHandshakeSuccess !== false
        );
    }

    // Screen mirroring is out of scope while a sharing mode already owns the session.
    // Both flags are keyed off session mode (not presentation state), so the button stays
    // hidden in canvas/scan/edit views too:
    //   - isApplicationSharingMode: application-sharing sessions (HY-65)
    //   - isDesktopSharingSession:  desktop-sharing sessions       (HY-626)
    @computed
    get displayScreenMirroringModeButton() {
        return (
            !this.isScreenMirroring &&
            !this.isApplicationSharingMode &&
            !this.isDesktopSharingSession &&
            !!this.clientRoomStorage.availableModes?.includes(MeetingMode.screen)
        );
    }

    isVideoOrScreensharingMode(mode: MeetingMode) {
        //@ts-ignore
        return utils.isVideoAvailableInMode(mode) || utils.isModeSupportNativeVideoSharing(mode);
    }

    @computed
    get displayLiveVideo() {
        return this.isVideoOrScreensharingMode(this.clientRoomStorage.mode) && this.areBothSidesConnected;
    }

    @computed
    get shouldDisplayPause() {
        const allowPauseByAgent = this.accountData.settings ? this.accountData.settings.allowPauseByAgent : true;
        return (
            allowPauseByAgent &&
            (!this.videoModeState.videoState.isPaused || this.videoModeState.videoState.videoPausedByDashboard) &&
            !this.isObserve &&
            this.videoModeState.videoState.focus &&
            this.videoModeState.videoState.enabled &&
            this.clientRoomStorage.mode !== MeetingMode.screen &&
            !(this.isToggleEnabled_ScanCaptureDesign && this.videoModeState.videoState.mode === VIDEO_MODES.SCANNING)
        );
    }

    @computed
    get displayButtonsInVideo() {
        return (
            !this.videoModeState.videoState.isPaused &&
            !this.videoModeState.videoState.videoPausedByDashboard &&
            !this.isObserve &&
            this.videoModeState.videoState.focus &&
            this.videoModeState.videoState.enabled &&
            (this.videoModeState.videoState.mode === VIDEO_MODES.SNAP ||
                this.videoModeState.videoState.mode === VIDEO_MODES.CURSOR_SHARING)
        );
    }

    @computed
    get isRecording() {
        return this.dashboardRoomStorage.recordingStatus === RECORDING_STATUS.started;
    }
    @computed
    get isMobileVisualScanProcessing() {
        return this.clientRoomStorage.visualScanStatus === VisualScanStatusType.InProgress;
    }
    @computed
    get isVisualScanInit() {
        return this.dashboardRoomStorage.visualScanStatus === VisualScanStatusType.Init;
    }
    @computed
    get isMobileWifiScanInProgress() {
        return this.clientRoomStorage.wifiScanStatus === WifiScanStatusType.InProgress;
    }
    @computed
    get isVisualScanPendingMobileApproval() {
        return !this.isMobileVisualScanProcessing && this.isVisualScanInit;
    }
    @computed
    get isWifiScanInProgress() {
        return this.dashboardRoomStorage.wifiScanStatus === WifiScanStatusType.InProgress;
    }

    get isToggleEnabled_NewLivePointerDesign() {
        return get(this.accountData, 'protectedSettings.enableNewLivePointerDesign');
    }

    get isVideoScan() {
        return this.videoModeState.videoState.mode === VIDEO_MODES.SCANNING;
    }

    get isImageScan() {
        return this.imageMode === IMAGE_MODES.SCANNING;
    }

    @computed
    get showScanners() {
        return this.accountData?.protectedSettings?.imageAnalysis || false;
    }
}

class WiFiAnalyzerState {
    @observable wifiScanResults?: IWifiAnalyzerResults;
    @observable visualScanResults?: IWifiAnalyzerResults;
}
